import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { getAccessToken } from "../utils/auth";
import { API_BASE_URL } from "../utils/api";
import { compareSwimmerNamesByLastName } from "../utils/swimmerSort";
import SwimmerTracker from "./SwimmerTracker";

const API = API_BASE_URL;
const WATCH_KEY = "fantasySwimWatchGroup";

const STROKE_ABBR = {
  Freestyle: "FR",
  Backstroke: "BK",
  Breaststroke: "BR",
  Butterfly: "FLY",
  "Individual Medley": "IM",
};

const STROKE_ORDER = [
  "Freestyle",
  "Backstroke",
  "Breaststroke",
  "Butterfly",
  "Individual Medley",
];

const toId = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const formatTime = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const seconds = Number(value);
  const minutes = Math.floor(seconds / 60);
  const remainder = (seconds % 60).toFixed(2).padStart(5, "0");
  return minutes > 0 ? `${minutes}:${remainder}` : remainder;
};

const renderTimesSummary = (bestTimes = {}) => {
  const parts = STROKE_ORDER.map((stroke) => {
    const time = bestTimes[stroke];
    if (time == null) return null;
    return `${STROKE_ABBR[stroke]} ${formatTime(time)}`;
  }).filter(Boolean);

  return parts.length ? parts.join("  •  ") : "No recorded times yet";
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const AGE_GROUP_OPTIONS = [
  { value: "all", label: "All Ages" },
  { value: "6u", label: "6 & Under" },
  { value: "7-8", label: "7-8" },
  { value: "9-10", label: "9-10" },
  { value: "11-12", label: "11-12" },
  { value: "13-14", label: "13-14" },
];

const GENDER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "boys", label: "Boys" },
  { value: "girls", label: "Girls" },
];

const getAgeGroupKey = (ageValue) => {
  const age = Number(ageValue);
  if (!Number.isFinite(age)) return "unknown";
  if (age <= 6) return "6u";
  if (age <= 8) return "7-8";
  if (age <= 10) return "9-10";
  if (age <= 12) return "11-12";
  if (age <= 14) return "13-14";
  return "unknown";
};

const getGenderKey = (genderValue) => {
  const value = String(genderValue || "")
    .trim()
    .toLowerCase();

  if (["m", "male", "boy", "boys"].includes(value)) return "boys";
  if (["f", "female", "girl", "girls"].includes(value)) return "girls";
  return "unknown";
};

const getHistoricalMeetSummaries = (swimmer) =>
  Array.isArray(swimmer?.meet_summaries) ? swimmer.meet_summaries : [];

const getProjectedMeetPoints = (swimmer) => {
  const meets = getHistoricalMeetSummaries(swimmer)
    .map((meet) => Number(meet?.raw_points))
    .filter((value) => Number.isFinite(value));

  if (!meets.length) return 0;
  return meets.reduce((sum, value) => sum + value, 0) / meets.length;
};

const parseFiltersFromSearch = (search = "") => {
  const params = new URLSearchParams(search);
  const searchTerm = params.get("q") || "";
  const ageGroupRaw = params.get("ageGroup") || "all";
  const genderRaw = params.get("gender") || "all";

  const ageGroupFilter = AGE_GROUP_OPTIONS.some(
    (option) => option.value === ageGroupRaw,
  )
    ? ageGroupRaw
    : "all";

  const genderFilter = GENDER_OPTIONS.some(
    (option) => option.value === genderRaw,
  )
    ? genderRaw
    : "all";

  return {
    searchTerm,
    ageGroupFilter,
    genderFilter,
  };
};

const SwimmerTrackerSummary = ({ hideNav = false, embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initializedFromUrl = useRef(false);
  const [allSwimmers, setAllSwimmers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [watchSet, setWatchSet] = useState(new Set());
  const [selectedSwimmer, setSelectedSwimmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const parsed = parseFiltersFromSearch(location.search);
    setSearchTerm(parsed.searchTerm);
    setAgeGroupFilter(parsed.ageGroupFilter);
    setGenderFilter(parsed.genderFilter);
    initializedFromUrl.current = true;
  }, [location.search]);

  useEffect(() => {
    if (!initializedFromUrl.current) return;

    const params = new URLSearchParams();
    const searchValue = searchTerm.trim();
    if (searchValue) params.set("q", searchValue);
    if (ageGroupFilter !== "all") params.set("ageGroup", ageGroupFilter);
    if (genderFilter !== "all") params.set("gender", genderFilter);

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;

    if (nextSearch === currentSearch) return;

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [
    searchTerm,
    ageGroupFilter,
    genderFilter,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setError(
        "You must be logged in to view swimmer tracking. Please log out and log back in.",
      );
      setLoading(false);
      return;
    }

    axios
      .get(`${API}/tracker/swimmers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        setAllSwimmers(res.data.swimmers || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err?.response?.status === 401) {
          localStorage.removeItem("fantasySwimAccessToken");
          localStorage.removeItem("fantasySwimSessionUser");
          setError(
            "Your session has expired or is invalid. Please log out and log back in.",
          );
        } else {
          setError(
            "Unable to load swimmer scouting data. Is the backend running?",
          );
        }
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WATCH_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        setWatchSet(new Set(parsed.filter((item) => typeof item === "string")));
      }
    } catch {
      setWatchSet(new Set());
    }
  }, []);

  const persistWatchSet = (nextSet) => {
    localStorage.setItem(WATCH_KEY, JSON.stringify(Array.from(nextSet)));
    setWatchSet(nextSet);
  };

  const addToWatch = (name) => {
    if (!name) return;
    const next = new Set(watchSet);
    next.add(name);
    persistWatchSet(next);
  };

  const removeFromWatch = (name) => {
    const next = new Set(watchSet);
    next.delete(name);
    persistWatchSet(next);
  };

  const toggleWatch = (name) => {
    if (watchSet.has(name)) {
      removeFromWatch(name);
      return;
    }
    addToWatch(name);
  };

  const clearAllWatch = () => {
    persistWatchSet(new Set());
  };

  const swimmersByName = useMemo(() => {
    const map = new Map();
    allSwimmers.forEach((swimmer) => {
      map.set(swimmer.name, swimmer);
    });
    return map;
  }, [allSwimmers]);

  const watchedSwimmers = useMemo(() => {
    return Array.from(watchSet)
      .map((name) => swimmersByName.get(name))
      .filter(Boolean)
      .sort((a, b) => compareSwimmerNamesByLastName(a?.name, b?.name));
  }, [watchSet, swimmersByName]);

  const filteredSwimmers = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return allSwimmers
      .filter((swimmer) => {
        const swimmerName = String(swimmer.name || "").toLowerCase();
        const matchesSearch =
          !normalizedSearchTerm || swimmerName.includes(normalizedSearchTerm);

        const matchesAgeGroup =
          ageGroupFilter === "all" ||
          getAgeGroupKey(swimmer.age) === ageGroupFilter;

        const matchesGender =
          genderFilter === "all" ||
          getGenderKey(swimmer.gender) === genderFilter;

        return matchesSearch && matchesAgeGroup && matchesGender;
      })
      .sort((a, b) => {
        const aPoints = getProjectedMeetPoints(a);
        const bPoints = getProjectedMeetPoints(b);
        if (bPoints !== aPoints) return bPoints - aPoints;
        return compareSwimmerNamesByLastName(a?.name, b?.name);
      });
  }, [allSwimmers, searchTerm, ageGroupFilter, genderFilter]);

  const renderOwner = (swimmer) => {
    if (swimmer.owned_by_other) {
      return (
        <span className="swimmer-summary-owned-other">
          Owned by {swimmer.coach}
        </span>
      );
    }
    if (swimmer.is_mine) {
      return <span className="swimmer-summary-owned-mine">On your team</span>;
    }
    return <span className="swimmer-summary-unowned">Unowned</span>;
  };

  const maxPoints = useMemo(
    () =>
      filteredSwimmers.length > 0
        ? Math.max(...filteredSwimmers.map((s) => getProjectedMeetPoints(s)), 1)
        : 1,
    [filteredSwimmers],
  );

  if (loading) {
    return <div className="swimmer-summary-loading">Loading swimmers…</div>;
  }

  if (error) {
    return (
      <div className="swimmer-summary-error">
        <strong>⚠ Could not load swimmers</strong>
        {error}
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? "swimmer-summary-page swimmer-summary-page-embedded"
          : "swimmer-summary-page"
      }
    >
      <div
        className={
          embedded
            ? "swimmer-summary-shell swimmer-summary-shell-embedded"
            : "swimmer-summary-shell"
        }
      >
        {!embedded && (
          <div className="swimmer-summary-topbar">
            <div>
              <h1 className="swimmer-summary-title">Swimmers</h1>
            </div>
            <div className="swimmer-summary-badge">
              <span className="swimmer-summary-badge-top">Swimmer</span>
              <span className="swimmer-summary-badge-value">
                {filteredSwimmers.length}
              </span>
              <span className="swimmer-summary-badge-bottom">Count</span>
            </div>
          </div>
        )}

        {!hideNav && (
          <div className="swimmer-summary-actions-bar">
            <button
              type="button"
              aria-pressed={location.pathname === "/swimmers/trend"}
              onClick={() => navigate("/swimmers/trend")}
              className={
                location.pathname === "/swimmers/trend"
                  ? "swimmer-summary-action-pill swimmer-summary-action-pill-active"
                  : "swimmer-summary-action-pill"
              }
            >
              <span className="swimmer-summary-action-icon">📈</span>
              Trend
            </button>
            <button
              type="button"
              aria-pressed={location.pathname === "/swimmers/available"}
              onClick={() => navigate("/swimmers/available")}
              className={
                location.pathname === "/swimmers/available"
                  ? "swimmer-summary-action-pill swimmer-summary-action-pill-active"
                  : "swimmer-summary-action-pill"
              }
            >
              <span className="swimmer-summary-action-icon">🌊</span>
              Available
            </button>
          </div>
        )}

        <div className="swimmer-summary-search-card">
          <div className="swimmer-summary-filter-group">
            <span className="swimmer-summary-filter-group-label">Search</span>
            <input
              type="text"
              className="swimmer-summary-search-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search swimmers by name"
              aria-label="Search swimmers by name"
            />
          </div>

          <div className="swimmer-summary-filter-group">
            <span className="swimmer-summary-filter-group-label">
              Age Group
            </span>
            <div className="swimmer-summary-filter-button-row">
              {AGE_GROUP_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={ageGroupFilter === option.value}
                  onClick={() => setAgeGroupFilter(option.value)}
                  className={
                    ageGroupFilter === option.value
                      ? "swimmer-summary-filter-btn swimmer-summary-filter-btn-active"
                      : "swimmer-summary-filter-btn"
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="swimmer-summary-filter-group">
            <span className="swimmer-summary-filter-group-label">Gender</span>
            <div className="swimmer-summary-filter-button-row">
              {GENDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={genderFilter === option.value}
                  onClick={() => setGenderFilter(option.value)}
                  className={
                    genderFilter === option.value
                      ? "swimmer-summary-filter-btn swimmer-summary-filter-btn-active"
                      : "swimmer-summary-filter-btn"
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="watch-sticky-card swimmer-summary-watch-card">
          <div className="swimmer-summary-watch-title-row">
            <h3 className="swimmer-summary-watch-title">Watch Group</h3>
            <div className="swimmer-summary-watch-actions-right">
              <button
                type="button"
                onClick={clearAllWatch}
                className="watch-clear-btn swimmer-summary-clear-all-btn"
                disabled={watchedSwimmers.length === 0}
              >
                Clear All
              </button>
              <span className="swimmer-summary-watch-count">
                {watchedSwimmers.length} watched
              </span>
            </div>
          </div>

          {watchedSwimmers.length > 0 && (
            <div className="swimmer-summary-watch-list">
              {watchedSwimmers.map((swimmer) => (
                <button
                  key={`pill-${toId(swimmer.name)}`}
                  type="button"
                  onClick={() => removeFromWatch(swimmer.name)}
                  className="swimmer-summary-watch-pill"
                  title="Remove from watch"
                >
                  {swimmer.name} ✕
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="swimmer-summary-section-head">
          <h2 className="swimmer-summary-section-title">Trending</h2>
        </div>

        {filteredSwimmers.length === 0 ? (
          <p className="swimmer-summary-empty-state">
            No swimmers match your current filters.
          </p>
        ) : (
          <div className="swimmer-summary-list">
            {filteredSwimmers.map((swimmer, idx) => {
              const isWatched = watchSet.has(swimmer.name);
              const projectedMeetPoints = getProjectedMeetPoints(swimmer);
              const attendance = formatPercent(swimmer.attendance_percentage);
              const barWidth = `${Math.max(8, Math.round((projectedMeetPoints / maxPoints) * 100))}%`;

              return (
                <article
                  key={toId(swimmer.name)}
                  className="swimmer-summary-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedSwimmer(swimmer)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedSwimmer(swimmer);
                    }
                  }}
                >
                  <button
                    type="button"
                    aria-pressed={isWatched}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWatch(swimmer.name);
                    }}
                    className={
                      isWatched
                        ? "swimmer-summary-round-watch-btn swimmer-summary-round-watch-btn-on"
                        : "swimmer-summary-round-watch-btn"
                    }
                    aria-label={
                      isWatched ? "Remove from watch" : "Add to watch"
                    }
                    title={isWatched ? "Watching" : "Add to watch"}
                  >
                    {isWatched ? "✓" : "○"}
                  </button>

                  <div className="swimmer-summary-rank-col">{idx + 1}</div>

                  <div className="swimmer-summary-main-col">
                    <div className="swimmer-summary-name-row">
                      <span className="swimmer-summary-name">
                        {swimmer.name}
                      </span>
                    </div>
                    <div className="swimmer-summary-owner-row">
                      {renderOwner(swimmer)}
                    </div>
                    <div className="swimmer-summary-meta-row">
                      <div className="swimmer-summary-meta-stat-row">
                        <div className="swimmer-summary-meta-stat-card">
                          <span className="swimmer-summary-meta-stat-label">
                            Age
                          </span>
                          <span className="swimmer-summary-meta-stat-value">
                            {swimmer.age ?? "—"}
                          </span>
                        </div>
                        <div className="swimmer-summary-meta-stat-card">
                          <span className="swimmer-summary-meta-stat-label">
                            Attendance
                          </span>
                          <span className="swimmer-summary-meta-stat-value">
                            {attendance}
                          </span>
                        </div>
                        <div className="swimmer-summary-meta-stat-card">
                          <span className="swimmer-summary-meta-stat-label">
                            Avg / Meet
                          </span>
                          <span className="swimmer-summary-meta-stat-value">
                            {projectedMeetPoints.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <span>
                        <span className="swimmer-summary-meta-label">
                          Top Times:
                        </span>{" "}
                        <span className="swimmer-summary-meta-value">
                          {renderTimesSummary(swimmer.best_times || {})}
                        </span>
                      </span>
                      <span className="swimmer-summary-card-hint">
                        Tap for full swimmer details
                      </span>
                    </div>
                  </div>

                  <div className="swimmer-summary-score-col">
                    <div className="swimmer-summary-score-text">
                      {projectedMeetPoints.toFixed(1)}
                    </div>
                    <div className="swimmer-summary-score-label">
                      Average Meet Points
                    </div>
                    <div className="swimmer-summary-score-track">
                      <div
                        className="swimmer-summary-score-fill"
                        style={{ width: barWidth }}
                      />
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {selectedSwimmer ? (
        <SwimmerTracker
          swimmer={selectedSwimmer}
          onClose={() => setSelectedSwimmer(null)}
        />
      ) : null}
    </div>
  );
};

export default SwimmerTrackerSummary;
